/**
 * PhaseBlocked Component
 * 
 * Displays a friendly message when a feature is blocked by phase rollout.
 * This is a SOFT block - shows informational message, not a hard 403.
 * Role-based access is still checked separately.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowLeft, Calendar } from 'lucide-react';
import { getPhaseName, getNextPhaseName, ACTIVE_PHASE } from '../../config/rolloutConfig';

const PhaseBlocked = ({ 
  requiredPhase, 
  featureName = 'This feature',
  showBackButton = true,
  backPath = '/agent/dashboard'
}) => {
  const navigate = useNavigate();
  const safePhase = requiredPhase || 'PHASE_1';
  const requiredPhaseName = getPhaseName(safePhase);
  const nextPhase = getNextPhaseName(safePhase);
  const currentPhaseName = getPhaseName(ACTIVE_PHASE);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
      <div className="max-w-md">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-full">
            <Sparkles className="w-10 h-10 text-indigo-500" />
          </div>
        </div>

        {/* Title */}
        <h1 className="mb-3 text-2xl font-bold text-gray-900">
          {featureName} Coming Soon
        </h1>

        {/* Description */}
        <p className="mb-6 text-gray-600">
          {featureName} is part of <strong>{requiredPhaseName}</strong> and will be available in the next phase of our rollout.
        </p>

        {/* Phase Info */}
        <div className="p-4 mb-6 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center justify-center mb-2 text-sm text-gray-500">
            <Calendar className="mr-2 w-4 h-4" />
            <span>Current Phase: <strong>{currentPhaseName}</strong></span>
          </div>
          {nextPhase && (
            <div className="text-sm text-gray-600">
              This feature will be available in <strong>{nextPhase}</strong>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          {showBackButton && (
            <button
              onClick={() => navigate(backPath)}
              className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg transition-colors hover:bg-indigo-700"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              Go Back
            </button>
          )}
          <button
            onClick={() => navigate('/agent/dashboard')}
            className="flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhaseBlocked;
